import type { WorkflowItem } from "@/api/models"

export type StartFieldType = "text" | "file"

/** A single input field declared on the start node (new multi-field form). */
export type StartField = {
  type: StartFieldType
  label: string
  required: boolean
  /** Stable key used to address the collected value in user_input_data. */
  key: string
}

type RawStartField = {
  type?: string
  label?: string
  required?: boolean
  key?: string
}

type StartNodeData = {
  fileMode?: "none" | "optional" | "required"
  requiresInput?: boolean
  fields?: RawStartField[]
}

type WorkflowContent = {
  nodes?: Array<{
    type?: string
    data?: StartNodeData
  }>
}

type WorkflowRunRequirements = {
  requiresInput: boolean
  showFileUpload: boolean
  filesRequired: boolean
  needsDialog: boolean
}

const defaultWorkflowRunRequirements: WorkflowRunRequirements = {
  requiresInput: false,
  showFileUpload: false,
  filesRequired: false,
  needsDialog: false,
}

/**
 * Derive a stable key for a field when the author hasn't set one explicitly.
 * Keys are `${type}_${index}` so legacy single-field flows are deterministic and
 * the backend can read `context.user_input_data[key]`.
 */
export function deriveStartFieldKey(type: StartFieldType, index: number): string {
  return `${type}_${index}`
}

/**
 * Resolve the *effective* input fields for a start node.
 *
 * - If `data.fields` is present and non-empty it is authoritative (new flows).
 * - Otherwise derive from the legacy `requiresInput` / `fileMode` booleans so
 *   existing flows behave exactly as before (back-compat, opt-in to fields[]).
 */
export function resolveStartFields(data: StartNodeData | null | undefined): StartField[] {
  const rawFields = data?.fields
  if (Array.isArray(rawFields) && rawFields.length > 0) {
    return rawFields.map((field, index) => {
      const type: StartFieldType = field.type === "file" ? "file" : "text"
      return {
        type,
        label: typeof field.label === "string" ? field.label : "",
        required: field.required === true,
        key: field.key && field.key.length > 0 ? field.key : deriveStartFieldKey(type, index),
      }
    })
  }

  // Legacy derivation: a text field when requiresInput, a file field when fileMode != "none".
  const fields: StartField[] = []
  if (data?.requiresInput === true) {
    fields.push({
      type: "text",
      label: "",
      required: true,
      key: deriveStartFieldKey("text", fields.length),
    })
  }
  const fileMode = data?.fileMode ?? "none"
  if (fileMode !== "none") {
    fields.push({
      type: "file",
      label: "",
      required: fileMode === "required",
      key: deriveStartFieldKey("file", fields.length),
    })
  }
  return fields
}

function getStartNodeData(
  workflow: Pick<WorkflowItem, "content"> | null | undefined
): StartNodeData | undefined {
  if (!workflow) return undefined
  try {
    const content = (
      typeof workflow.content === "string" ? JSON.parse(workflow.content) : workflow.content
    ) as WorkflowContent
    return content.nodes?.find((node) => node.type === "start")?.data
  } catch {
    return undefined
  }
}

export function getWorkflowRunRequirements(
  workflow: Pick<WorkflowItem, "content" | "file_required"> | null | undefined
): WorkflowRunRequirements {
  if (!workflow) return defaultWorkflowRunRequirements

  const showLegacyFileUpload = workflow.file_required === true
  const startNodeData = getStartNodeData(workflow)
  const fields = resolveStartFields(startNodeData)

  // Derive aggregate requirements from the effective fields so needsDialog and the
  // dialog rendering stay in sync regardless of whether fields[] or legacy data is used.
  const textFields = fields.filter((field) => field.type === "text")
  const fileFields = fields.filter((field) => field.type === "file")

  const requiresInput = textFields.some((field) => field.required)
  const showFileUpload = fileFields.length > 0 || showLegacyFileUpload
  const filesRequired = fileFields.some((field) => field.required) || showLegacyFileUpload

  return {
    requiresInput,
    showFileUpload,
    filesRequired,
    needsDialog: fields.length > 0 || showFileUpload,
  }
}

export function workflowRequiresInput(
  workflow: Pick<WorkflowItem, "content" | "file_required">
): boolean {
  return getWorkflowRunRequirements(workflow).needsDialog
}
